import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { transcriptKeys } from "./queryKeys";
import type { CompanyOption, JobOption, MatchOption } from "@/types/transcript";

// Options for the linking panel's match/job/company searches. Fetched once
// per panel open and cached, instead of the previous unbounded refetch on
// every dialog interaction. Org scoping comes from RLS, like the rest of
// the app's direct table reads.

const OPTIONS_LIMIT = 200;
const OPTIONS_STALE_TIME = 5 * 60_000;

export function useMatchOptions(enabled: boolean) {
	return useQuery({
		queryKey: transcriptKeys.entityOptions("matches"),
		queryFn: async (): Promise<MatchOption[]> => {
			const { data, error } = await supabase
				.from("matches")
				.select("id, candidates(name), jobs(title, companies(name))")
				.order("created_at", { ascending: false })
				.limit(OPTIONS_LIMIT);
			if (error)
				throw error;
			type Row = {
				id: string;
				candidates: { name: string | null } | null;
				jobs: { title: string | null; companies: { name: string | null } | null } | null;
			};
			return ((data || []) as Row[]).map((m) => ({
				id: m.id,
				candidate_name: m.candidates?.name || "",
				job_title: m.jobs?.title || "",
				company_name: m.jobs?.companies?.name || "",
			}));
		},
		enabled,
		staleTime: OPTIONS_STALE_TIME,
	});
}

export function useJobOptions(enabled: boolean) {
	return useQuery({
		queryKey: transcriptKeys.entityOptions("jobs"),
		queryFn: async (): Promise<JobOption[]> => {
			const { data, error } = await supabase
				.from("jobs")
				.select("id, title, companies(name)")
				.order("created_at", { ascending: false })
				.limit(OPTIONS_LIMIT);
			if (error)
				throw error;
			type Row = { id: string; title: string | null; companies: { name: string | null } | null };
			return ((data || []) as Row[]).map((j) => ({
				id: j.id,
				title: j.title || "",
				company_name: j.companies?.name || "",
			}));
		},
		enabled,
		staleTime: OPTIONS_STALE_TIME,
	});
}

export function useCompanyOptions(enabled: boolean) {
	return useQuery({
		queryKey: transcriptKeys.entityOptions("companies"),
		queryFn: async (): Promise<CompanyOption[]> => {
			const { data, error } = await supabase
				.from("companies")
				.select("id, name, city, country")
				.order("created_at", { ascending: false })
				.limit(OPTIONS_LIMIT);
			if (error)
				throw error;
			type Row = { id: string; name: string | null; city: string | null; country: string | null };
			return ((data || []) as Row[]).map((c) => ({
				id: c.id,
				name: c.name || "",
				city: c.city || "",
				country: c.country || "",
			}));
		},
		enabled,
		staleTime: OPTIONS_STALE_TIME,
	});
}
